/**
 * V113-7-4 / D-113-29 — "Sign a new pack" with carried exceptions. A pack signed over open problems carries them:
 * a reason and a tick per carried group are required (the server answers 400 `exceptions_unacknowledged` with the
 * exact groups otherwise; the dialog then shows those). Systems with no class stay out; inferred classes are listed
 * with their Confirm link. Owner / admin (the caller gates the opener).
 */
import { useEffect, useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Checkbox, Field, Textarea } from '@aigovops/design-system';
import { useQueryClient } from '@tanstack/react-query';
import type { CarriedCounts, CarriedGroup, PackValidity } from '@aigovops/core';
import { ActionDialog } from '../today/shared';
import { monitoringErrorOf, signPackWithExceptions } from '../../lib/monitoring-api';
import type { MonitoringSummaryRow } from '../../lib/monitoring-summary';
import { monitoringKeys } from '../../hooks/useMonitoring';
import { carriedText, CLASS_ANCHOR, classLabel, systemTabHref } from '../../lib/monitoring-view';
import { fmtDate, plural } from '../../lib/format';
import { StatusChip } from './parts';

const FAILING_CODES = new Set(['control_degradation', 'ci_out_of_scope_failing']);
const OVERDUE_CODES = new Set(['monitoring_testing_overdue', 'monitoring_evidence_stale']);

export interface CarriedPreview { counts: CarriedCounts; systems: Record<CarriedGroup, string[]> }

/** What a re-sign would carry, from the current validity (the server's 400 corrects it if it differs). */
export function previewCarried(validity: PackValidity | null): CarriedPreview {
  const counts: CarriedCounts = { failing: 0, overdue: 0 };
  const systems: Record<CarriedGroup, Set<string>> = { failing: new Set(), overdue: new Set() };
  for (const r of validity?.reasons ?? []) {
    const g: CarriedGroup | null = FAILING_CODES.has(r.code) ? 'failing' : OVERDUE_CODES.has(r.code) ? 'overdue' : null;
    if (!g) continue;
    counts[g] += r.count;
    for (const it of r.items) if (it.system_id) systems[g].add(it.system_id);
  }
  // Exceptions an earlier pack carried and still open are carried again.
  if (validity?.carried) {
    counts.failing += validity.carried.failing;
    counts.overdue += validity.carried.overdue;
    for (const [sid, c] of Object.entries(validity.carried_by_system ?? {})) {
      if (c.failing) systems.failing.add(sid);
      if (c.overdue) systems.overdue.add(sid);
    }
  }
  return { counts, systems: { failing: [...systems.failing], overdue: [...systems.overdue] } };
}

export function SignPackDialog({ open, slug, prefix, validity, previousSignedAt, rows, onClose, onSigned }: {
  open: boolean; slug: string; prefix: string; validity: PackValidity | null; previousSignedAt: string | null;
  rows: MonitoringSummaryRow[]; onClose: () => void; onSigned: (message: string) => void;
}) {
  const uid = useId();
  const qc = useQueryClient();
  const initial = useMemo(() => previewCarried(validity), [validity]);
  const [preview, setPreview] = useState<CarriedPreview>(initial);
  const [ticks, setTicks] = useState<Record<CarriedGroup, boolean>>({ failing: false, overdue: false });
  const [reason, setReason] = useState('');
  const [errs, setErrs] = useState<{ reason?: string; ticks?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setPreview(initial); setTicks({ failing: false, overdue: false }); setReason(''); setErrs({}); setError(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const names = new Map(rows.map((r) => [r.system_id, r.name]));
  const groups = (['failing', 'overdue'] as CarriedGroup[]).filter((g) => preview.counts[g] > 0);
  const carrying = groups.length > 0;
  const unclassed = rows.filter((r) => !r.system_class);
  const inferred = rows.filter((r) => r.system_class && r.system_class_source === 'inferred');
  const missing = carrying && (!reason.trim() || groups.some((g) => !ticks[g]));
  const hintId = `${uid}-hint`;

  const groupLine = (g: CarriedGroup) => {
    const n = preview.counts[g];
    const what = g === 'failing' ? plural(n, 'failing control') : plural(n, 'overdue check');
    const who = preview.systems[g].map((s) => names.get(s) ?? '').filter(Boolean).join(', ');
    return `I accept ${what}${who ? ` · ${who}` : ''}`;
  };

  return (
    <ActionDialog open={open} title="Sign a new pack" submitLabel="Sign new pack" onClose={onClose} busy={busy} error={error}
      describedBy={missing ? hintId : undefined}
      pinned={missing ? <p id={hintId} className="mon-hint">Add a reason and tick each group</p> : null}
      onSubmit={async () => {
        const e: typeof errs = {};
        if (carrying && !reason.trim()) e.reason = 'Say why the pack is signed with open problems.';
        if (carrying && groups.some((g) => !ticks[g])) e.ticks = 'Tick each carried group.';
        setErrs(e);
        if (e.reason || e.ticks) {
          // U-9: say what's missing where it is: focus the first unticked group, else the reason.
          const firstGroup = groups.find((g) => !ticks[g]);
          setTimeout(() => document.getElementById(e.ticks && firstGroup ? `${uid}-${firstGroup}` : `${uid}-reason`)?.focus(), 0);
          return false;
        }
        setBusy(true); setError(null);
        try {
          const out = await signPackWithExceptions(slug, carrying ? { reason: reason.trim(), acknowledge: groups } : {});
          await qc.invalidateQueries({ queryKey: monitoringKeys.root(slug) });
          const carried = carriedText(preview.counts);
          onSigned(`Pack v${out.package.version} signed${carried ? ` · ${carried} carried` : ''}`);
          return true;
        } catch (err) {
          const f = monitoringErrorOf(err);
          if (f.kind === 'exceptions_unacknowledged' && f.carried) {
            // The server names exactly what would be carried: show those groups and ask again.
            setPreview({ counts: f.carried, systems: preview.systems });
            setTicks({ failing: false, overdue: false });
            setError('The pack would carry open problems. Add a reason and tick each group.');
          } else if (f.kind === 'forbidden') setError('Only an owner or admin can sign the pack.');
          else if (f.kind === 'not_configured') setError('Signing isn’t set up for this workspace yet.');
          else setError(f.message || 'The pack could not be signed.');
          return false;
        } finally { setBusy(false); }
      }}>
      <p className="mon-line mon-line--text">
        <span>Recipients will see</span>
        <StatusChip label={carrying ? 'Current with exceptions' : 'Current'} tone={carrying ? 'amber' : 'outline'} glyph={carrying ? 'warning' : 'check'} />
        {carrying ? <strong>{carriedText(preview.counts)}</strong> : null}
      </p>
      {carrying ? (
        <fieldset className="mon-reasons" aria-describedby={errs.ticks ? `${uid}-terr` : undefined}>
          <legend className="mon-eyebrow">Carried as exceptions · tick each</legend>
          {groups.map((g) => (
            <Checkbox key={g} id={`${uid}-${g}`} checked={ticks[g]} label={groupLine(g)}
              onChange={(e) => { const checked = e.currentTarget.checked; setTicks((t) => ({ ...t, [g]: checked })); setErrs((x) => ({ ...x, ticks: undefined })); }} />
          ))}
          {errs.ticks ? <p id={`${uid}-terr`} className="mon-err" role="alert">{errs.ticks}</p> : null}
        </fieldset>
      ) : null}
      <Field id={`${uid}-reason`} label={carrying ? 'Reason' : 'Reason (optional)'} required={carrying} error={errs.reason}>
        <Textarea rows={3} value={reason} placeholder="e.g. Renewal due Oct 31; guardrail fix ships Oct 24"
          onChange={(e) => { setReason(e.currentTarget.value); setErrs((x) => ({ ...x, reason: undefined })); }} />
      </Field>
      {unclassed.length || inferred.length ? (
        <dl className="mon-dl">
          {unclassed.length ? (<><dt>Stays out</dt><dd>{unclassed.map((r, i) => (
            <span key={r.system_id}>{i ? ' · ' : ''}{r.name} (no class) · <Link to={systemTabHref(prefix, r.system_id, CLASS_ANCHOR)}>Set class</Link></span>
          ))}</dd></>) : null}
          {inferred.length ? (<><dt>Class not confirmed</dt><dd>{inferred.map((r, i) => (
            <span key={r.system_id}>{i ? ' · ' : ''}{r.name} · {classLabel(r.system_class)} · <Link to={systemTabHref(prefix, r.system_id, CLASS_ANCHOR)}>Confirm class</Link></span>
          ))}</dd></>) : null}
        </dl>
      ) : null}
      {previousSignedAt ? <p className="mon-hint">Replaces the pack attested {fmtDate(previousSignedAt)} · its history and digest stay visible</p> : null}
    </ActionDialog>
  );
}
